// Adoption Score DÉCLARÉ — l'aperçu de la mesure zéro, sur la fiche de l'étude
// « mesure zéro ».
//
// Le calcul est fait par le SERVEUR (même agrégat que le score prouvé, sur les
// notes déclarées) et figé en instantané. Rien n'est calculé ici : ce composant
// affiche ce que le serveur a enregistré, et propose de le recalculer.
//
// Il ne se confond jamais avec le score prouvé : autre libellé, tracé en
// pointillés sur le radar, et la mention explicite « déclaré ».
import { useComputeMesure0, useProductContext } from '../api/queries';
import { ApiRequestError } from '../api/client';
import { Button, Eyebrow, Note } from '../components/ui';
import { useCanWrite } from '../write/useCanWrite';
import './mesure0.css';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function Mesure0Score({ projectId }: { projectId: string }) {
  const context = useProductContext(projectId);
  const compute = useComputeMesure0(projectId);
  const canWrite = useCanWrite(projectId);

  // Rien à montrer tant qu'aucun pilier n'est noté ET qu'aucun calcul n'existe :
  // le bouton « Calculer » apparaît quand la mesure zéro le permet.
  if (!context.data) return null;
  const c = context.data;
  const baseline = c.baseline;
  if (c.declaredScored === 0 && !baseline) return null;

  // Les notes ont-elles bougé depuis le dernier instantané ? Alors on invite à
  // recalculer, sans jamais modifier l'instantané en silence.
  const current = new Map(c.pillars.map((p) => [p.id, p.declared_score]));
  const stale = baseline
    ? baseline.pillars.length !== c.pillars.length
      || baseline.pillars.some((p) => current.get(p.pillarId) !== p.score)
    : false;

  // Mise en forme pour le radar existant : seuls les piliers NOTÉS y figurent.

  const button = canWrite ? (
    <Button variant={baseline && !stale ? 'ghost' : 'accent'} loading={compute.isPending} onClick={() => compute.mutate()}>
      {baseline ? 'Recalculer la mesure 0' : 'Calculer l’Adoption Score déclaré'}
    </Button>
  ) : null;

  return (
    <section className="zy-card m0-score" aria-labelledby="m0-score-title">
      <div className="m0-head">
        <div>
          <Eyebrow>Adoption Score · mesure 0 (déclaré)</Eyebrow>
          <h2 className="t-h3" id="m0-score-title">
            {baseline ? 'Ce que le client estime de son produit' : 'Les piliers sont notés : calculez la mesure 0'}
          </h2>
          <p className="card-lede">
            {baseline
              ? 'Moyenne pondérée des notes déclarées par le client. Ce n’est pas une preuve : c’est le point de départ que vos études vont confronter.'
              : `${c.declaredScored} pilier${c.declaredScored > 1 ? 's' : ''} sur ${c.pillars.length} ${c.declaredScored > 1 ? 'ont' : 'a'} une note déclarée. Le calcul fige un instantané daté.`}
          </p>
        </div>
        {button}
      </div>

      {compute.error ? (
        <Note tone="erreur" role="alert">
          {compute.error instanceof ApiRequestError ? compute.error.message : 'Le calcul a échoué.'}
        </Note>
      ) : null}

      {baseline ? (
        <>
          {stale ? (
            <Note tone="avertissement">Les notes ont changé depuis ce calcul. Recalculez pour mettre l’aperçu à jour.</Note>
          ) : null}
          <div className="m0-score-body">
            <div className="m0-score-big">
              <span className="big">
                {baseline.score ?? '—'}
                <span className="big-unit">/100</span>
              </span>
              <p className="zy-texte-muet">Calculé le {formatDate(baseline.computedAt)}</p>
            </div>

            <ol className="m0-score-list">
              {baseline.pillars.map((p) => (
                <li key={p.pillarId}>
                  <span className="m0-score-name">{p.name}</span>
                  <span className="pillar-bar" aria-hidden="true">
                    <span className="pillar-fill m0-fill" style={{ width: `${(p.score ?? 0) * 10}%` }} />
                  </span>
                  <span className="m0-score-val">
                    {p.score === null ? 'non noté' : `${p.score.toLocaleString('fr-FR')}/10`}
                  </span>
                  <span className="pillar-weight">×{p.weight}</span>
                </li>
              ))}
            </ol>
          </div>
        </>
      ) : null}
    </section>
  );
}
